import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

/**
 * Signed-in customer's order history. Fetches /orders/mine (auth-gated) and
 * renders each paid order as a compact boutique card with a "View details"
 * link that reuses the existing tracking page.
 */
export default function MyOrders() {
  const { user, loading: authLoading } = useAuth();
  const [orders, setOrders] = useState(null);

  useEffect(() => {
    if (authLoading) return;
    if (!user) return;
    api.get("/orders/mine")
      .then(({ data }) => setOrders(data.orders || []))
      .catch(() => setOrders([]));
  }, [user, authLoading]);

  if (authLoading) {
    return (
      <main className="min-h-screen flex items-center justify-center text-espresso-light label-xs">
        Loading…
      </main>
    );
  }
  if (!user) {
    return (
      <main
        className="min-h-screen flex items-center justify-center"
        data-testid="my-orders-signin-gate"
      >
        <div className="text-center max-w-md px-6">
          <div className="label-xs text-espresso-light">My orders</div>
          <h1 className="mt-3 font-serif text-4xl text-espresso">
            Sign in to see your orders.
          </h1>
          <Link to="/login?next=/my-orders" className="btn-espresso inline-block mt-8">
            Sign in
          </Link>
        </div>
      </main>
    );
  }

  return (
    <main data-testid="my-orders-page" className="min-h-screen bg-ivory">
      <div className="max-w-[1100px] mx-auto px-6 md:px-10 py-14 md:py-20">
        <div className="label-xs text-espresso-light">Account</div>
        <div className="flex items-baseline justify-between flex-wrap gap-4 mt-4">
          <h1 className="font-serif text-4xl md:text-5xl text-espresso">My orders</h1>
          <Link
            to="/account"
            data-testid="link-account"
            className="label-xs text-espresso-light link-underline"
          >
            Manage account →
          </Link>
        </div>

        {orders === null ? (
          <div className="mt-10 label-xs text-espresso-light">Loading…</div>
        ) : orders.length === 0 ? (
          <div
            className="mt-14 border border-line p-10 text-center"
            data-testid="my-orders-empty"
          >
            <p className="font-serif italic text-espresso text-lg">No orders yet.</p>
            <p className="mt-2 text-sm text-espresso-light">
              Your future spoons will show up here.
            </p>
            <Link to="/menu" className="btn-espresso inline-block mt-6">
              Browse menu
            </Link>
          </div>
        ) : (
          <div className="mt-10 space-y-4" data-testid="my-orders-list">
            {orders.map((o) => (
              <div
                key={o.id}
                className={
                  "border border-line p-5 md:p-6 bg-ivory flex flex-col md:flex-row "
                  + "md:items-center md:justify-between gap-4"
                }
                data-testid={`order-row-${o.order_number}`}
              >
                <div className="min-w-0">
                  <div className="label-xs text-espresso-light">
                    {(o.created_at || "").slice(0, 10)} · {o.fulfillment}
                  </div>
                  <div className="mt-1 font-serif text-xl text-espresso">{o.order_number}</div>
                  <div className="mt-1 text-sm text-espresso-light truncate">
                    {(o.items || [])
                      .slice(0, 3)
                      .map((it) => `${it.quantity}× ${it.name}`)
                      .join(" · ")}
                    {(o.items || []).length > 3 && ` · +${(o.items || []).length - 3} more`}
                  </div>
                </div>
                <div className="flex flex-wrap items-center gap-3 flex-shrink-0 md:gap-4">
                  <div className="text-right">
                    <div className="font-serif text-xl text-espresso">
                      ₹{Math.round(o.total || 0)}
                    </div>
                    <div className="label-xs text-espresso-light capitalize">{o.status}</div>
                  </div>
                  <Link
                    to={`/track/${o.order_number}`}
                    className={
                      "border border-espresso text-espresso px-4 py-2 uppercase tracking-[0.2em] "
                      + "text-[10px] hover:bg-espresso hover:text-ivory transition-colors"
                    }
                    data-testid={`view-order-${o.order_number}`}
                  >
                    Details
                  </Link>
                  <a
                    href={
                      `${process.env.REACT_APP_BACKEND_URL}/api/orders/`
                      + `${o.order_number}/invoice.pdf`
                    }
                    target="_blank"
                    rel="noreferrer"
                    className={
                      "border border-espresso px-4 py-2 text-[10px] uppercase tracking-[0.2em] "
                      + "text-espresso transition-colors duration-200 hover:bg-espresso "
                      + "hover:text-ivory"
                    }
                    data-testid={`download-gst-invoice-${o.order_number}`}
                  >
                    GST invoice
                  </a>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </main>
  );
}
